import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { entitySideDataTableStyles } from 'app/shared/componentsRestyled/common/form/shared/styles';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { ImpoundCategoryType } from 'constants/impoundTypes';
import useSort from 'hooks/useSort';
import { getConditionalObject, getOptionTypeName } from 'utils/data';
import { filterUsedOptions } from 'utils/loanApplication';

import {
  selectExistingImpoundTypesOptionsByCategory,
  selectImpoundTypesOptionsByCategory,
} from 'store/system/selectors';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';
import { tableWrapperStyles } from '../../../Content.styles';
import { getNotFoundTableData, prepareModifiedFields } from '../../../Content.utils';
import { pagesPropTypes } from '../../MainContent.constants';
import { propertyExpensesModifiedFieldsToExclude } from '../PropertyExpenses.constants';
import { createNewTableData, getValuesToSave } from '../PropertyExpenses.utils';

import { getTableConfig } from './SupplementalInsurance.config';
import {
  defaultSortOrder,
  SupplementalInsuranceInfo,
  TableColumnId,
} from './SupplementalInsurance.constants';
import SupplementalInsuranceForm from './SupplementalInsuranceForm';

const defaultObject = {};

const defaultPendingChanges = {};

const SupplementalInsurance = ({
  isLoading,
  isHidden = false,
  form = defaultObject,
  supplementalInsurances: supplementalInsurancesTableData = null,
  setEntitySideData,
  setInitialEntitySideData,
  entitySideData,
  initialEntitySideData = defaultObject,
  setIsSavingAllowed,
  pendingChanges,
  setIsNestedEntityEditActive,
}) => {
  const [editableData, setEditableData] = useState(null);

  const { order, onRequestSort } = useSort(defaultSortOrder);

  const impoundTypeOptions = useSelector(selectImpoundTypesOptionsByCategory);
  const existingImpoundTypeOptions = useSelector(selectExistingImpoundTypesOptionsByCategory);

  const supplementalInsuranceTypeOptions =
    impoundTypeOptions[ImpoundCategoryType.SUPPLEMENTAL_INSURANCE] || [];
  const existingSupplementalInsuranceTypeOptions =
    existingImpoundTypeOptions[ImpoundCategoryType.SUPPLEMENTAL_INSURANCE] || [];

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? supplementalInsurancesTableData : form;
  const isDataEmpty = _.isEmpty(entityData);
  const isDataLoading = isLoading && isDataEmpty;
  const { supplementalInsurances: supplementalInsurancesData } = entitySideData;
  const supplementalInsurances = supplementalInsurancesData?.map(insurance => ({
    ...insurance,
    name: getOptionTypeName(supplementalInsuranceTypeOptions, insurance.impoundTypeId),
    value: insurance.impoundTypeId,
  }));

  const filteredSupplementalInsurances = filterUsedOptions({
    data: supplementalInsurances,
    options: existingSupplementalInsuranceTypeOptions,
    currentOption: editableData?.impoundTypeId,
  });

  const isEditActive = !!editableData;
  const isSupplementalInsurancesExist = !!supplementalInsurances?.length;
  const pendingChangesData =
    pendingChanges?.supplementalInsurancesPendingChanges || defaultPendingChanges;

  useEffect(() => {
    setIsSavingAllowed(!isEditActive);
    setIsNestedEntityEditActive(isEditActive);

    return () => {
      setIsSavingAllowed(true);
    };
  }, [isEditActive, setIsSavingAllowed, setIsNestedEntityEditActive]);

  const supplementalInsurancesInitialValuesMap = useMemo(() => {
    return initialEntitySideData.supplementalInsurances
      ? _.keyBy(initialEntitySideData.supplementalInsurances, 'id')
      : {};
  }, [initialEntitySideData]);

  const orderedSupplementalInsurancesWithPendingChanges = useMemo(() => {
    const orderedSupplementalInsurances = _.orderBy(
      supplementalInsurances,
      [order.id],
      [order.direction],
    );

    return prepareModifiedFields({
      currentData: orderedSupplementalInsurances,
      pendingChangesData,
      initialValuesMap: supplementalInsurancesInitialValuesMap,
      fieldsToExclude: propertyExpensesModifiedFieldsToExclude,
    });
  }, [
    order.direction,
    order.id,
    pendingChangesData,
    supplementalInsurances,
    supplementalInsurancesInitialValuesMap,
  ]);

  const correctedSupplementalInsurancesTableData = isSupplementalInsurancesExist
    ? orderedSupplementalInsurancesWithPendingChanges
    : getNotFoundTableData(TableColumnId.IMPOUND_TYPE_ID);

  useEffect(() => {
    if (!supplementalInsurancesTableData) {
      return;
    }

    const isSupplementalInsurancesExist = _.has(entitySideData, SupplementalInsuranceInfo.KEY);

    if (isSupplementalInsurancesExist) {
      return;
    }

    const dataToSet = {
      ...getConditionalObject(!isSupplementalInsurancesExist, {
        supplementalInsurances: supplementalInsurancesTableData,
      }),
    };

    setEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
  }, [
    entitySideData,
    supplementalInsurancesTableData,
    setEntitySideData,
    setInitialEntitySideData,
  ]);

  const deleteTableData = useCallback(
    id => {
      setEntitySideData(prevState => ({
        ...prevState,
        supplementalInsurances: prevState?.supplementalInsurances.reduce((acc, entity) => {
          if (entity.id !== id) {
            acc.push(entity);

            return acc;
          }

          if (entity.isAdded) {
            return acc;
          }

          acc.push({ ...entity, isDeleted: true });

          return acc;
        }, []),
      }));

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const tableConfig = getTableConfig({
    supplementalInsuranceTypeOptions,
    setEditableData,
    deleteTableData,
    editableRowId: editableData?.id,
    isActionDisabled: isLoading,
  });

  const addNewTableEntity = useCallback(() => {
    const entityData = createNewTableData();

    setEntitySideData(prevState => ({
      ...prevState,
      supplementalInsurances: [...(prevState?.supplementalInsurances ?? []), entityData],
    }));

    setEditableData(entityData);
  }, [setEntitySideData]);

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        setEntitySideData(prev => ({
          ...prev,
          supplementalInsurances: prev.supplementalInsurances.filter(({ id }) => id !== data.id),
        }));
      }

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const dataToSave = getValuesToSave({ updatedData, initialData });

      setEntitySideData(prevState => ({
        ...prevState,
        supplementalInsurances: prevState?.supplementalInsurances?.map(supplementalInsurance =>
          supplementalInsurance.id === dataToSave.id ? dataToSave : supplementalInsurance,
        ),
      }));

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const additionalRowContent = editableData ? (
    <SupplementalInsuranceForm
      cancelForm={cancelForm}
      data={editableData}
      deleteTableData={deleteTableData}
      isLoading={isLoading}
      pendingChanges={pendingChangesData[editableData.id]}
      saveForm={saveForm}
      supplementalInsuranceTypeOptions={filteredSupplementalInsurances}
    />
  ) : null;

  // Hidden for a chattel loan (LEN-156): the rows are still seeded above but never edited, so the
  // subject-property save sends no supplemental-insurance changes.
  if (isHidden) {
    return null;
  }

  return (
    <>
      <TableCDS
        isHideTableWhenEmpty
        additionalRowContent={additionalRowContent}
        additionalRowContentRowId={editableData?.id}
        config={tableConfig}
        customStyles={entitySideDataTableStyles}
        data={correctedSupplementalInsurancesTableData}
        isCustomDataDefinition={!isSupplementalInsurancesExist}
        isLoading={isDataLoading}
        isSortDisabled={isEditActive}
        order={order}
        pendingChangesTextSelector="span"
        title={SupplementalInsuranceInfo.TITLE}
        wrapperStyle={tableWrapperStyles}
        onRequestSort={onRequestSort}
      />
      {!isLoading && (
        <div className="mt-24 pb-24">
          <Button
            data-testid={PropertyAndLoanTestIds.SUPPLEMENTAL_INSURANCE_ADD_BUTTON}
            disabled={isEditActive || isLoading}
            size="medium"
            startIcon={<AddBoxIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={addNewTableEntity}
          >
            Add {SupplementalInsuranceInfo.TITLE}
          </Button>
        </div>
      )}
    </>
  );
};

const usedProps = [
  'className',
  'entitySideData',
  'form',
  'initialEntitySideData',
  'isLoading',
  'pendingChanges',
  'setEntitySideData',
  'setInitialEntitySideData',
  'setIsSavingAllowed',
  'setIsNestedEntityEditActive',
];

SupplementalInsurance.propTypes = {
  ..._.pick(pagesPropTypes, usedProps),
  isHidden: PropTypes.bool,
  supplementalInsurances: PropTypes.array,
};

export default SupplementalInsurance;
